import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { NavigationStepPathEnum } from '@/constants/navigation';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { PaymentDetailsInputType } from '@/types';
import { openAffirmModal, validateAffirmBillingFields } from '@/utils/payment/affirm';
import { formatStripeToken, validateStripeBillingFields } from '@/utils/payment/stripe';
import { scrollToElement } from '@/utils/scrollToElement';
import { useRouter } from 'next/navigation';
import appInfo from '../../package.json';
import { useCreateBooking } from './useCreateBooking';
import { ReactTagManager } from 'react-gtm-ts';

export const usePaymentProcessing = () => {
  const stripe = useStripe();
  const elements = useElements();
  const router = useRouter();

  const {
    addToPaymentSlice,
    billingAddress,
    cardName,
    cardNumberComplete,
    cardExpiryComplete,
    cardCvcComplete,
    clearErrors,
    discount,
    dob,
    email,
    errorsPayment,
    firstname,
    gender,
    holdEventId,
    isErrors,
    lastname,
    locationId,
    machineId,
    medicalReferral,
    paymentMethodType,
    phoneNumber,
    preferredName,
    referrerName,
    referrerPhoneNumber,
    setError,
    setErrorModal,
    setLogInDialog,
    sku,
    utcEnd,
    utcStart,
    studyId,
    finalPrice,
  } = usePrenuvoStore(
    useShallow((state) => ({
      addToPaymentSlice: state.payment.methods.add,
      billingAddress: state.payment.billingAddress,
      cardName: state.payment.cardName,
      cardNumberComplete: state.payment.cardNumberComplete,
      cardExpiryComplete: state.payment.cardExpiryComplete,
      cardCvcComplete: state.payment.cardCvcComplete,
      clearErrors: state.payment.methods.clearErrors,
      discount: state.payment.discount,
      dob: state.user.dob,
      email: state.user.email,
      errorsPayment: state.payment.errors,
      firstname: state.user.firstName,
      gender: state.user.gender,
      holdEventId: state.scan.eventId,
      isErrors: state.payment.methods.isErrors,
      lastname: state.user.lastName,
      locationId: state.scan.locationId,
      machineId: state.scan.machineId,
      medicalReferral: state.user.medicalReferral,
      paymentMethodType: state.payment.paymentMethodType,
      phoneNumber: state.user.phoneNumber,
      preferredName: state.user.preferredName,
      referrerName: state.user.referrerName,
      referrerPhoneNumber: state.user.referrerPhoneNumber,
      setError: state.payment.methods.setError,
      setErrorModal: state.ui.methods.setErrorModal,
      setLogInDialog: state.ui.methods.setLogInDialog,
      utcEnd: state.scan.slot?.utcEnd,
      utcStart: state.scan.slot?.utcStart,
      sku: state.scan.sku,
      studyId: state.scan.session?.studyId,
      finalPrice: state.payment.finalPrice,
    })),
  );

  const setServerErrorModal = () => {
    setErrorModal(
      'Server Error',
      'An unexpected error has occurred on our servers. Please try again later.',
    );
  };

  const { trigger: triggerCreateBooking, isMutating } = useCreateBooking({
    onError: () => setServerErrorModal(),
  });

  const [preventSubmit, setPreventSubmit] = useState(false);

  // GTM Analytics
  const ecommerceData = useMemo(
    () => ({
      event: 'purchase',
      ecommerce: {
        currencyCode: sku?.price?.currency,
        purchase: {
          actionField: {
            id: studyId,
            affiliation: '',
            revenue: finalPrice, // incl. referral values
            tax: '',
            shipping: '',
            coupon: '',
          },
          products: [
            {
              name: sku?.name,
              id: `${sku?.skuID}`,
              price: `${sku?.price?.total}`,
              brand: locationId,
              category: 'scan',
              variant: machineId,
              quantity: '1',
              coupon: '',
            },
          ],
        },
      },
    }),
    [finalPrice, locationId, machineId, sku, studyId],
  );

  const handleCreateBooking = useCallback(
    async (paymentDetailsInput: PaymentDetailsInputType) => {
      const bookingInput = {
        locationId: locationId,
        bookingSource: `${appInfo.name}_${appInfo.version}`,
        member: {
          dob: dob,
          email: email,
          firstname: firstname,
          gender: gender,
          lastname: lastname,
          medicalReferral: medicalReferral === 'true',
          referrerName: referrerName,
          referrerPhoneNumber: referrerPhoneNumber,
          phoneNumber: phoneNumber,
          preferredName: preferredName,
        },
        promoCode: discount?.promoID,
        holdEventId: holdEventId,
        skuId: Number(sku?.skuID),
        timeSlot: {
          machineId: machineId,
          utcEnd: utcEnd,
          utcStart: utcStart,
        },
      };

      const createBookingPayload = {
        bookingInput,
        paymentDetailsInput,
      };

      const response = await triggerCreateBooking(createBookingPayload);

      // if response is an empty object, it means the server is down (502 error)
      const responseIsAnEmptyObject =
        Object.keys(response).length === 0 && response.constructor === Object;

      if (response?.data?.errors || response?.errors || responseIsAnEmptyObject) {
        setServerErrorModal();
        return false;
      }

      const { status, message, booking } = response.data.createBooking;

      if (message && status !== 200) {
        // 444 is the status code for existing user
        if (status === 444) {
          setLogInDialog(true);
          return;
        }
        setErrorModal('Payment Processing Error', message);
        return false;
      }
      if (!isMutating && status === 200) {
        if (booking?.transactionId) {
          addToPaymentSlice({ transactionId: booking?.transactionId });
          ReactTagManager.action(ecommerceData);
        }
        return true;
      }
      return false;
    },
    [
      addToPaymentSlice,
      discount?.promoID,
      dob,
      ecommerceData,
      email,
      firstname,
      gender,
      holdEventId,
      isMutating,
      lastname,
      locationId,
      machineId,
      medicalReferral,
      phoneNumber,
      preferredName,
      referrerName,
      referrerPhoneNumber,
      setErrorModal,
      setLogInDialog,
      sku?.skuID,
      triggerCreateBooking,
      utcEnd,
      utcStart,
    ],
  );

  const handleErrors = useCallback(
    (errors: any) => {
      // TODO:  refactor to use zod-validation-error to handle errors
      Object.entries(errors.flatten()?.fieldErrors as [string, string[]][])?.forEach((error) => {
        const [path, message] = error;
        setError(path, message ? message[0] : ('' as string));
      });
      scrollToElement(errors.errors[0]?.path?.[0]);
    },
    [setError],
  );

  const handleStripeElementError = useCallback(() => {
    if (!cardNumberComplete || errorsPayment?.cardNumber) {
      setError('cardNumber', 'Your card number is incomplete.');
      scrollToElement('cardNumber');
    }
    if (!cardExpiryComplete || errorsPayment?.cardExpiry) {
      setError('cardExpiry', "Your card's expiration date is incomplete.");
      scrollToElement('cardExpiry');
    }
    if (!cardCvcComplete || errorsPayment?.cardCvc) {
      setError('cardCvc', "Your card's security code is incomplete.");
      scrollToElement('cardCvc');
    }
  }, [cardNumberComplete, cardExpiryComplete, cardCvcComplete, errorsPayment, setError]);

  const getStripeToken = useCallback(async () => {
    if (!(stripe && elements)) {
      // Stripe.js has not yet loaded.
      setError('token', 'Payment Error');

      return false;
    }
    const cardElement: any = elements.getElement(CardNumberElement);
    const { token, error } = await stripe.createToken(cardElement, {
      name: cardName,
      address_country: billingAddress.country,
      address_zip: billingAddress.postalCode,
    });
    if (error) {
      return false;
    }
    return token;
  }, [stripe, elements, cardName, billingAddress.country, billingAddress.postalCode, setError]);

  const handleAffirmPayment = async () => {
    validateAffirmBillingFields(
      {
        country: billingAddress.country,
        postalCode: billingAddress.postalCode,
        state: billingAddress.state,
        city: billingAddress.city,
        address1: billingAddress.address1,
      },
      handleErrors,
    );
    if (isErrors()) return false;

    const customer = {
      address: {
        country: billingAddress.country,
        zipcode: billingAddress.postalCode,
        state: billingAddress.state,
        city: billingAddress.city,
        line1: billingAddress.address1,
        line2: '',
      },
      email: email,
      name: {
        first: firstname,
        last: lastname,
      },
      phone_number: phoneNumber,
    };

    const AffirmModalPayload = {
      customer,
      sku,
      studyId,
      finalPrice,
    };

    openAffirmModal(AffirmModalPayload, AffirmModalOnSuccess, AffirmModalOnErrors);
  };

  const AffirmModalOnSuccess = async (token: string) => {
    const result = await handleCreateBooking({
      paymentType: 'external lender',
      paymentMethod: 'affirm',
      affirmToken: token,
    });
    // redirect to confirmation page if create booking is successful
    if (result) router.push(`${NavigationStepPathEnum.CONFIRMATION}`);
  };

  const AffirmModalOnErrors = () => {
    setErrorModal(
      'Affirm Application Cancelled',
      'We were unable to complete the Affirm financing process. Please pay by credit or debit card or give us a call at 1-833-733-6886',
    );
  };

  const handleStripePayment = async () => {
    handleStripeElementError();
    validateStripeBillingFields(
      {
        country: billingAddress.country,
        postalCode: billingAddress.postalCode,
        cardName: cardName,
      },
      handleErrors,
    );
    if (isErrors()) return false;
    const stripeToken = await getStripeToken();
    if (!stripeToken) return false;
    const paymentDetailsInput = formatStripeToken(stripeToken);

    if (paymentDetailsInput) return await handleCreateBooking(paymentDetailsInput);
  };

  const handleSubmit = async () => {
    if (preventSubmit) return false;

    setPreventSubmit(true);
    clearErrors();

    try {
      if (paymentMethodType === 'stripe') return await handleStripePayment();
      if (paymentMethodType === 'affirm') return await handleAffirmPayment();
      return false;
    } finally {
      setPreventSubmit(false);
    }
  };

  return {
    handleSubmit,
    isMutating,
  };
};
